/**
 * Navbar — fixed chrome + the signature "weld seam": a 2px accent bar at the
 * very top whose width tracks page scroll. Driven by rAF + direct style writes
 * (no React state) so it costs nothing during scroll.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import BrandLogo from './BrandLogo';
import { isAuthed } from '../lib/api';
import { getTheme, toggleTheme, type Theme } from '../lib/theme';

export default function Navbar() {
  const seamRef = useRef<HTMLDivElement>(null);
  const [solid, setSolid] = useState(false);
  // Keep the first client render identical to the prerendered public shell.
  // Browser-only preferences are applied immediately after hydration.
  const [theme, setThemeState] = useState<Theme>('light');
  const [authed, setAuthed] = useState(false);

  useEffect(() => {
    setThemeState(getTheme());
    setAuthed(isAuthed());
  }, []);

  useEffect(() => {
    let raf = 0;
    let lastSolid = false;
    const tick = (): void => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(1, window.scrollY / max) : 0;
      if (seamRef.current) seamRef.current.style.width = `${p * 100}%`;
      const nowSolid = window.scrollY > 24;
      if (nowSolid !== lastSolid) { lastSolid = nowSolid; setSolid(nowSolid); }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <>
      <div className="weld-seam" ref={seamRef} aria-hidden="true" />
      <header className={`navbar${solid ? ' is-solid' : ''}`}>
        <Link to="/" className="navbar-logo" aria-label="Piixel — home">
          <BrandLogo variant="full" height={26} />
        </Link>
        <div className="navbar-right">
          <nav className="navbar-links" aria-label="Main">
            <a href="/#features">Features</a>
            <a href="/#compare">How it works</a>
            <a href="/#visual-editor">Visual editor</a>
            <a href="/#pricing">Pricing</a>
          </nav>
          <span className="navbar-divider" aria-hidden="true" />
          {/* account / utility cluster */}
          <div className="navbar-utils">
            <button
              type="button"
              className="theme-toggle"
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              onClick={() => setThemeState(toggleTheme())}
            >
              {theme === 'dark' ? '☀' : '☾'}
            </button>
            {authed ? (
              <Link to="/dashboard" className="navbar-cta">Dashboard</Link>
            ) : (
              <>
                <Link to="/login" className="navbar-login">Log in</Link>
                <Link to="/signup" className="navbar-cta">Start</Link>
              </>
            )}
          </div>
        </div>
      </header>
    </>
  );
}
